import { useEffect } from "react";
import { IMAGES } from "../images/images"
import { Phone, Users, Wrench, Clock, Star, Package, CheckCircle } from "lucide-react";
import { trackPhoneConversion } from "../utils/tracking";

const PHONE = "+359876995651";
const PHONE_DISPLAY = "Обадете се";

const features = [
  { icon: Users, title: "Оценка на необходимия брой хамали", desc: "Правим точна оценка на теглото и обема на вашия товар, за да изпратим правилния брой специалисти." },
  { icon: Wrench, title: "Специализирано оборудване", desc: "Разполагаме с необходимите инструменти за безопасно пренасяне на тежки и габаритни мебели." },
  { icon: Clock, title: "Гъвкавост при планирането", desc: "Адаптираме се към вашия график и осигуряваме плавно преместване в удобно за вас време." },
  { icon: CheckCircle, title: "Предварителен оглед", desc: "Преди преместването нашите експерти оглеждат и оценяват всички предмети за максимална сигурност." },
  { icon: Star, title: "Персонализиран подход", desc: "Всеки клиент е уникален. Предлагаме решения, съобразени точно с вашите нужди и изисквания." },
  { icon: Package, title: "Разглобяване и сглобяване", desc: "Разглобяваме и сглобяваме мебели при необходимост — спални, кухни, шкафове и килими." },
];

const steps = [
  { num: "01", title: "Обаждате се", desc: "Свържете се с нас по телефона за безплатна консултация и уточняване на детайлите." },
  { num: "02", title: "Безплатен оглед", desc: "Изпращаме специалист за оглед на място и точна ценова оферта без ангажимент." },
  { num: "03", title: "Пренасяне", desc: "В уговорения ден и час нашите хамали пристигат и пренасят вещите бързо и безопасно." },
  { num: "04", title: "Готово!", desc: "Всичко е на новото място — наредено, сглобено и готово за употреба." },
];

export default function HamalskiUslugiPage() {
  useEffect(() => {
    document.title = "Хамалски услуги София | Пренасяне на мебели — Хамали София";
    const setMeta = (name, content, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setCanonical = (url) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", url);
    };
    setMeta("description", "Хамалски услуги в София — пренасяне на мебели, уреди и тежки предмети. Опитен екип, специализирано оборудване, безплатен оглед. Обадете се: +359 876 995 651");
    setMeta("og:title", "Хамалски услуги София | Пренасяне на мебели — Хамали София", true);
    setMeta("og:description", "Пренасяне на мебели и тежки предмети в София. Опитни хамали, специализирано оборудване, разглобяване и сглобяване на мебели. Безплатен оглед.", true);
    setMeta("og:url", "https://hamalski-sofia.com/hamalski-uslugi", true);
    setCanonical("https://hamalski-sofia.com/hamalski-uslugi");
  }, []);

  return (
    <div style={{ backgroundColor: "#FFFFFF", paddingTop: "68px" }}>

      {/* HERO — dark */}
      <section data-testid="hamalski-hero" style={{ position: "relative", minHeight: "70vh", display: "flex", alignItems: "center", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${IMAGES.HeroHome})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="hero-overlay" />
        <div style={{ position: "relative", zIndex: 2, maxWidth: "1280px", margin: "0 auto", padding: "4rem 1.25rem", width: "100%" }}>
          <p style={{ color: "#2dcf0c", fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1rem" }}>Хамалски услуги — София</p>
          <h1 data-testid="hamalski-h1" style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 1.05, letterSpacing: "-0.02em", color: "#FFFFFF", textTransform: "uppercase", maxWidth: "700px", marginBottom: "1.5rem" }}>
            Професионални Хамалски Услуги в{" "}
            <span style={{ color: "#2dcf0c" }}>София</span>
          </h1>
          <p style={{ color: "#d4d4d8", fontSize: "1.05rem", lineHeight: 1.7, maxWidth: "560px", marginBottom: "2rem" }}>
            Опитен екип хамали за пренасяне на мебели, уреди, тежки предмети и цялостно преместване на домакинство в София и областта.
          </p>
          <a href={`tel:${PHONE}`} data-testid="hamalski-call-btn" className="btn-call" style={{ padding: "1rem 2.25rem", fontSize: "1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
            <Phone size={20} />
            <span>{PHONE_DISPLAY}</span>
          </a>
        </div>
      </section>

      {/* DESCRIPTION — white */}
      <section style={{ padding: "4rem 1.25rem", maxWidth: "900px", margin: "0 auto" }}>
        <p style={{ fontSize: "1.075rem", color: "#374151", lineHeight: 1.85, borderLeft: "3px solid #2dcf0c", paddingLeft: "1.5rem" }}>
          <strong style={{ color: "#0A0A0A" }}>Хамали София</strong> предоставя цялостни хамалски услуги в столицата и близките области. Специализираме в товаро-разтоварни дейности, пренасяне на тежки предмети, мебели и бяла техника. Предлагаме индивидуален подход към всеки клиент — от малки апартаменти до цели домакинства.
        </p>
      </section>

      {/* FEATURES — light grey */}
      <section data-testid="hamalski-features" style={{ backgroundColor: "#F8F8FA", borderTop: "1px solid rgba(0,0,0,0.06)", padding: "4rem 1.25rem" }}>
        <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", textAlign: "center", marginBottom: "3rem" }}>
            Защо нашите хамали са <span style={{ color: "#2dcf0c" }}>различни</span>
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1.25rem" }}>
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} data-testid={`feature-${f.title.split(' ').slice(0,2).join('-').toLowerCase()}`}
                  style={{ padding: "1.75rem", backgroundColor: "#FFFFFF", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "8px", boxShadow: "0 2px 16px rgba(0,0,0,0.05)", transition: "box-shadow 0.25s ease, border-color 0.25s ease" }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 24px rgba(45,207,12,0.12)"; e.currentTarget.style.borderColor = "rgba(45,207,12,0.3)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 2px 16px rgba(0,0,0,0.05)"; e.currentTarget.style.borderColor = "rgba(0,0,0,0.08)"; }}>
                  <div style={{ width: 44, height: 44, backgroundColor: "rgba(45,207,12,0.08)", border: "1px solid rgba(45,207,12,0.2)", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "1rem" }}>
                    <Icon size={20} color="#2dcf0c" />
                  </div>
                  <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "0.95rem", color: "#0A0A0A", marginBottom: "0.5rem", textTransform: "uppercase" }}>{f.title}</h3>
                  <p style={{ color: "#6B7280", fontSize: "0.875rem", lineHeight: 1.65 }}>{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS — white */}
      <section data-testid="hamalski-process" style={{ padding: "4rem 1.25rem", maxWidth: "1280px", margin: "0 auto" }}>
        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3vw, 2.25rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", textAlign: "center", marginBottom: "3rem" }}>
          Как работим
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1.5rem" }}>
          {steps.map((s) => (
            <div key={s.num} style={{ textAlign: "center", padding: "2rem 1.25rem", backgroundColor: "#FFFFFF", border: "1px solid rgba(0,0,0,0.07)", borderRadius: "8px", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize: "3rem", fontFamily: "Montserrat, sans-serif", fontWeight: 900, color: "rgba(45,207,12,0.3)", lineHeight: 1, marginBottom: "0.75rem" }}>{s.num}</div>
              <h3 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800, fontSize: "1rem", color: "#0A0A0A", textTransform: "uppercase", marginBottom: "0.5rem" }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: "0.875rem", lineHeight: 1.65 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section data-testid="hamalski-cta" style={{ padding: "4rem 1.25rem", textAlign: "center", backgroundColor: "#F0FDF4", borderTop: "1px solid rgba(45,207,12,0.2)" }}>
        <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)", color: "#0A0A0A", textTransform: "uppercase", letterSpacing: "-0.02em", marginBottom: "1rem" }}>
          Готови за преместване? <span style={{ color: "#2dcf0c" }}>Обадете се!</span>
        </h2>
        <p style={{ color: "#6B7280", marginBottom: "2rem", fontSize: "1rem" }}>Безплатен оглед и оферта. Работим 7 дни в седмицата — София и областта.</p>
        <a href={`tel:${PHONE}`} data-testid="hamalski-cta-btn" className="btn-call" style={{ padding: "1.1rem 2.5rem", fontSize: "1.1rem", borderRadius: "4px" }} onClick={trackPhoneConversion}>
          <Phone size={22} />
          <span>{PHONE_DISPLAY}</span>
        </a>
      </section>
    </div>
  );
}
